import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send } from "lucide-react"; // Install: npm install lucide-react
import BASE_URL from "../BASEURL";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BASE_URL}/api/inquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setShowSuccessModal(true);
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    document.title = "Contact Us - Gujarat State APP Association 2025";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = "Get in touch with the Gujarat State Assistant Public Prosecutors Association (GSAPPPA) 2025. Find our contact information, address, and reach out for inquiries. Located at Gujarat High Court Campus, Ahmedabad Prosecutors Association contact details.";
    }
    
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.href = "https://gsappa.kevalontechnology.in/contact";
    } else {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = 'https://gsappa.kevalontechnology.in/contact';
      document.head.appendChild(canonical);
    }
    
    // Update OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.content = "Contact Us - Gujarat State APP Association 2025";
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) ogDescription.content = "Get in touch with the Gujarat State Assistant Public Prosecutors Association (GSAPPPA) 2025. Find our contact information, address, and reach out for inquiries. Located at Gujarat High Court Campus, Ahmedabad Prosecutors Association contact details.";
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.content = "https://gsappa.kevalontechnology.in/contact";
    
    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) twitterTitle.content = "Contact Us - Gujarat State APP Association 2025";
    
    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) twitterDescription.content = "Get in touch with the Gujarat State Assistant Public Prosecutors Association (GSAPPPA) 2025. Find our contact information, address, and reach out for inquiries. Located at Gujarat High Court Campus, Ahmedabad Prosecutors Association contact details.";
    
    const twitterUrl = document.querySelector('meta[property="twitter:url"]');
    if (twitterUrl) twitterUrl.content = "https://gsappa.kevalontechnology.in/contact";
  }, []);
  const contactInfo = {
    address: "B/6 APP OFFICE AHMEDABAD RURAL COURT , NAVRANGPURA , AHMEDABAD - 380014", // Update with real address
    email: "tusharbarot85@gmail.com", // Placeholder – replace with actual email
    phone: "+91 98799 60984", // Example from previous member (Tushar L. Barot) – change to official number
    workingHours: "Monday – Saturday: 10:00 AM – 6:00 PM",
  };

  const quickContacts = [
    { role: "President", name: "Kirit V. Chaudhary", phone: "+91 9978544909" },
    { role: "Vice President", name: "Dharmeshkumar S. Thakor", phone: "+91 9879264099" },
    // Add more if needed
  ];

  const container = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
  };

  const item = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen text-gray-800">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#0B0B0B] via-[#1A1A1A] to-[#0B0B0B] text-white py-40 md:py-48 text-center overflow-hidden">
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <motion.h1
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.1 }}
            className="text-5xl md:text-7xl font-extrabold text-[#D4A017] tracking-tight"
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="mt-6 text-xl md:text-2xl text-gray-200 font-medium"
          >
            THE GUJARAT STATE ASSISTANT PUBLIC PROSECUTORS ASSOCIATION – 2025
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 1 }}
            className="mt-4 text-lg text-gray-300 max-w-3xl mx-auto"
          >
            We are here to assist you. Reach out for any queries, suggestions, or collaboration.
          </motion.p>
        </div>
      </section>

      {/* Contact Information + Form */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
          {/* Left – Contact Details */}
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-10"
          >
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0B0B0B] mb-8">
                Get in Touch
              </h2>
              <div className="space-y-6">
                <motion.div variants={item} className="flex items-start gap-4">
                  <MapPin className="w-7 h-7 text-[#D4A017] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">Address</h3>
                    <p className="text-gray-700 mt-1">{contactInfo.address}</p>
                  </div>
                </motion.div>

                <motion.div variants={item} className="flex items-center gap-4">
                  <Mail className="w-7 h-7 text-[#D4A017] flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">Email</h3>
                    <a
                      href={`mailto:${contactInfo.email}`}
                      className="text-gray-700 hover:text-[#D4A017] transition-colors"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </motion.div>

                <motion.div variants={item} className="flex items-center gap-4">
                  <Phone className="w-7 h-7 text-[#D4A017] flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">Phone</h3>
                    <a
                      href={`tel:${contactInfo.phone.replace(/\s+/g, "")}`}
                      className="text-gray-700 hover:text-[#D4A017] transition-colors"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                </motion.div>

                <motion.div variants={item} className="text-sm text-gray-600 italic">
                  Working Hours: {contactInfo.workingHours}
                </motion.div>
              </div>
            </div>

            {/* Quick Contacts */}
            <div>
              <h3 className="text-2xl font-bold text-[#0B0B0B] mb-6">Key Contacts</h3>
              <div className="space-y-4">
                {quickContacts.map((contact, idx) => (
                  <motion.div
                    key={idx}
                    variants={item}
                    className="bg-white p-5 rounded-xl shadow-md border-l-4 border-[#D4A017]"
                  >
                    <p className="font-semibold text-gray-900">{contact.role}</p>
                    <p className="text-gray-700">{contact.name}</p>
                    <p className="text-gray-600 text-sm mt-1">Ph: {contact.phone}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right – Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border-t-4 border-[#D4A017]"
          >
            <h2 className="text-3xl font-bold text-[#0B0B0B] mb-8">Send us a Message</h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-100 text-red-700 p-3 rounded-lg">
                  {error}
                </div>
              )}
              <div>
                <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50"
                  placeholder="Your name"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50"
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-gray-700 font-medium mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50"
                  placeholder="How can we help you?"
                  required
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full px-5 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50"
                  placeholder="Your message here..."
                  required
                ></textarea>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4A017] text-[#0B0B0B] font-bold py-4 px-6 rounded-lg shadow-md hover:bg-[#b58917] transition-colors flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#0B0B0B]"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </motion.button>
            </form>

            <p className="text-center text-sm text-gray-500 mt-6">
              We will get back to you within 2–3 working days.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-[#0B0B0B] py-16 text-center text-white">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto px-6"
        >
          <h3 className="text-3xl md:text-4xl font-bold mb-6">
            Dedicated to the Service of Justice
          </h3>
          <p className="text-xl text-gray-300">
            Your voice matters. Connect with us today.
          </p>
        </motion.div>
      </section>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full mx-4 text-center">
            <div className="text-green-500 text-6xl mb-4">✓</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Message Sent Successfully!</h2>
            <p className="text-gray-600 mb-6">Thank you for contacting us. We'll get back to you within 2-3 working days.</p>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="bg-[#D4A017] text-[#0B0B0B] font-bold py-3 px-6 rounded-lg hover:bg-[#b58917] transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;